import React, { useCallback, useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { TheChatBot, SignInMarkerComponent, StickyChatBotOpener } from '@pcln/penny-chat-bot'
import pclnExpt from '@pcln/experimentation'
import { getNegotiatorapp } from '@mfs/client/utils/getNegotiatorapp'
import { logEvent } from '../common/log-ga-event/logGaEvent'

import { getFlightsDetails, getFlightsDetailsForListing, getInfoForLogging } from './utils'
import ErrorBoundary from '@pcln/error-boundary'
import { usePartner } from '@pcln/whitelabel-components'

export const handleMarkerResponse = (signedIn) => (err, markerText) => {
  if (!err && markerText && markerText === 'PROMPT_SIGN_IN') {
    return {
      role: 'assistant',
      content: '',
      promptContent: 'Displayed signin marker component.',
      markerComponentProps: { signedIn },
    }
  }

  return {
    role: 'assistant',
    content: 'Sorry, looks like something is wrong, please try again.',
  }
}

const getMarkerComponents = () => {
  return {
    PROMPT_SIGN_IN: SignInMarkerComponent,
  }
}

const ga4Attributes = {
  type: 'penny',
  page_name: 'details',
  product_name: 'flight',
}

export const TravelChatBot = (props) => {
  const { details, pclnBootstrapData, isProgress, pageSource } = props ?? {}
  const [areChatBotFeaturesLoaded, setAreChatBotFeaturesLoaded] = useState(false)

  const handleChatBotFeaturesLoaded = () => {
    setAreChatBotFeaturesLoaded(true)
  }

  const { isMobile, requestLocals, webstats, locale, customer, authUser } = pclnBootstrapData

  const partnerData = usePartner()

  const isPartner = Boolean(partnerData)

  const isWLPennyEnabled = Boolean(requestLocals?.features?.whiteLabel?.pennyEnabled)

  const isPennyEnabledInWLPartner = isPartner && isWLPennyEnabled

  const isUserSignedIn = Boolean(authUser)

  const markerResponseHandler = handleMarkerResponse(isUserSignedIn)

  const logFirstMessageSubmitted = /* istanbul ignore next */ () => {
    logEvent('begin_chat', ga4Attributes)
  }

  const { webstats: webstatsFromRequestLocals } = requestLocals ?? {}
  const { clientGUID: cguid } = webstatsFromRequestLocals || webstats || {}

  const isListingsPage = pageSource === 'listings'
  const [isChatBoxOpened, setIsChatBoxOpened] = useState(false)
  const isNegotiatorapp = getNegotiatorapp() === 'y'

  const showPenny = (!isPartner || isPennyEnabledInWLPartner) && !isProgress && !isNegotiatorapp

  const revalidationSetiName = isListingsPage
    ? 'PENNY_FLIGHT_LISTINGS_REVALIDATION'
    : 'PENNY_FLIGHT_DETAILS_REVALIDATION'
  // Fire on page eligibility (same audience as the FAB), not gated on isProgress —
  // matches relax-ui / drive-search. Still exclude partner / negotiator.
  const wouldShowRevalidationFab = (!isPartner || isPennyEnabledInWLPartner) && !isNegotiatorapp
  const isPennyFabHiddenByRevalidation =
    wouldShowRevalidationFab && pclnExpt.getTestState(revalidationSetiName) === 'VARIANT'

  useEffect(() => {
    if (wouldShowRevalidationFab) {
      // immediateImpression bypasses listings idle queue so the impression is not dropped
      pclnExpt.fireImpression(revalidationSetiName, {}, { immediateImpression: true })
    }
  }, [wouldShowRevalidationFab, revalidationSetiName])

  const onChatDialogRendered = useCallback(() => {
    logEvent('initiate_chat', ga4Attributes)
  }, [])

  const placementName = isListingsPage ? 'flightListings' : 'flightDetail'

  const errorBoundaryPayload = {
    page: isListingsPage ? 'flight-listing' : 'flight-details',
    componentName: 'TravelChatBot',
    props,
  }

  let numOfTravellers = 0
  if (isListingsPage) {
    const { numOfAdults, numOfChildren } = details?.loggingInfo || {}
    numOfTravellers = +numOfAdults + +numOfChildren
  } else {
    const { ADULT, CHILD } = details?.passengers || {}
    numOfTravellers = +ADULT + +CHILD
  }

  let flightDetails
  let loggingInfo
  try {
    flightDetails = isListingsPage
      ? getFlightsDetailsForListing(details.listings, {
          numOfTravellers,
          travelDate: details?.loggingInfo?.travelDate,
          returnDate: details?.loggingInfo?.returnDate,
          loggingInfo: details?.loggingInfo || {},
        })
      : getFlightsDetails(details, undefined, {
          numOfTravellers,
        })
  } catch (_e) {
    flightDetails = ''
  }
  try {
    loggingInfo = isListingsPage
      ? getInfoForLogging(details.loggingInfo || {}, pageSource)
      : getInfoForLogging(details)
  } catch (_e) {
    loggingInfo = {}
  }

  const generateChatBotRguid = () => {
    const generateRguidUuid = uuidv4()
    if (isPartner) {
      const partnerCode = partnerData?.partnerCode || ''
      return `${placementName}-whitelabel-${partnerCode}-${generateRguidUuid}`
    }
    return generateRguidUuid
  }

  if (showPenny) {
    const componentVersion = pclnBootstrapData?.moduleInfo?.['@pcln/penny-chat-bot']
    return (
      <ErrorBoundary payload={errorBoundaryPayload}>
        <>
          <TheChatBot
            viewType={isListingsPage ? 'FLYLISTINGS' : 'FLYDETAILS'}
            componentVersion={componentVersion}
            customerName={customer?.firstName}
            handleMarkerResponse={markerResponseHandler}
            markerComponents={getMarkerComponents()}
            isChatBoxOpened={isChatBoxOpened && areChatBotFeaturesLoaded}
            locale={locale}
            onChatDialogRendered={onChatDialogRendered}
            logFirstMessageSubmitted={logFirstMessageSubmitted}
            onClose={() => {
              setIsChatBoxOpened(false)
            }}
            startingChatMessagesPayload={{
              flightPayload: {
                details: flightDetails,
                ...loggingInfo,
              },
              returnJSONResponse: true,
              productId: 1,
              rguid: generateChatBotRguid(),
              cguid: `${cguid}`,
              freezeKey: 'n/a',
              dateToday: new Date().toString(),
            }}
            webstats={webstats}
          />
          {/* Sticky Icon to open the chat modal */}
          {!isChatBoxOpened && !isPennyFabHiddenByRevalidation && (
            <StickyChatBotOpener
              openerTooltipProps={{
                center: false,
                right: true,
              }}
              disablePopTip
              fabPosition={{
                zIndex: '1',
                bottom: isMobile && !isListingsPage ? '150px' : undefined,
              }}
              onComponentRendered={() => {
                logEvent('display', ga4Attributes)
              }}
              isChatBoxOpened={isChatBoxOpened}
              onClickHandler={
                /* istanbul ignore next */ () => {
                  handleChatBotFeaturesLoaded()
                  setIsChatBoxOpened(true)
                }
              }
            />
          )}
        </>
      </ErrorBoundary>
    )
  }
  return null
}

TravelChatBot.displayName = 'TravelChatBot'
